import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import React, { createContext, ReactNode, useContext, useMemo, useState } from "react";

import {
  ContextMenu,
  ContextMenuProviderState,
  ContextMenuState,
} from "./context-menu";

const DEFAULT_CONTEXT_MENU_STATE: ContextMenuState = {
  element: null,
  menu: [],
  showContextMenu: async () => "",
};

const ContextMenuContext = createContext<ContextMenuState>(
  DEFAULT_CONTEXT_MENU_STATE,
);

interface ContextMenuProviderProps {
  children: ReactNode;
}

function ContextMenuProvider(props: ContextMenuProviderProps) {
  const [state, updateState] = useState<ContextMenuProviderState>(DEFAULT_CONTEXT_MENU_STATE),
    { children } = props,
    { element, menu } = state,
    onDismissContextMenu = (selection: string = "") => {
      const { resolve } = state;

      updateState(DEFAULT_CONTEXT_MENU_STATE);

      if (resolve) {
        resolve(selection);
      }
    },
    onShowContextMenu = async (element: Element, menu: ContextMenu[]) => {
      return new Promise<string>(resolve => {
        updateState({
          element: element,
          menu: menu,
          resolve: resolve,
        });
      });
    },
    value: ContextMenuState = useMemo(
      () => ({
        ...state,
        showContextMenu: onShowContextMenu,
      }),
      [state],
    );

  return (
    <ContextMenuContext.Provider value={value}>
      <>
        {children}
        <Menu
          anchorEl={element}
          id="context-menu"
          onClose={onDismissContextMenu.bind(null, "")}
          open={Boolean(element && menu.length)}
        >
          {menu.map(item => {
            const { label, value, color, disabled, tooltip } = item,
              menuItem = (
                <MenuItem
                  disabled={disabled}
                  key={value}
                  onClick={disabled ? undefined : onDismissContextMenu.bind(null, value)}
                  style={color ? { color: color } : undefined}
                >
                  {label}
                </MenuItem>
              );

            if (!tooltip) {
              return menuItem;
            }

            return (
              <Tooltip
                key={value}
                title={tooltip}
              >
                {/* A disabled MenuItem has pointer-events: none, so the tooltip
                    needs a wrapper element to catch hover events. */}
                <span>{menuItem}</span>
              </Tooltip>
            );
          })}
        </Menu>
      </>
    </ContextMenuContext.Provider>
  );
}

function useContextMenu() {
  const { showContextMenu } = useContext(ContextMenuContext);

  return { showContextMenu };
}

export { ContextMenuProvider, useContextMenu };
